import { createSelector } from 'reselect';
import { BaseSelectors } from '@makemydeal/dr-activities-common';
import type { PdfTradeIn } from '@makemydeal/dr-platform-types';
import type { DealType, IncentiveType } from '@makemydeal/dr-shared-bff-types';
import type { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import type { AdditionalSelectors, ProposalCustomerInfo, RequestProposalState } from '../types';
import { mapCustomer, mapSalesPersonName } from '../utils/proposalMappers';
import { hasSingleVehicle } from './multi-scenario/selectors';

type RootStateWithProposal = { requestProposal: RequestProposalState };

const NO_TRADES: PdfTradeIn[] = [];

/**
 * Carries the host-provided AdditionalSelectors into the activity's store singletons
 * (dependency injection). Mirrors the dealership-picker Selectors class.
 */
export class Selectors extends BaseSelectors {
    constructor(public sliceName: string, public additionalSelectors: AdditionalSelectors) {
        super(sliceName);
    }

    getSalesPersonInfo = (state: any): string => mapSalesPersonName(this.additionalSelectors.getSalesPerson(state));

    getCustomerInfo = createSelector(
        (state: any) => this.additionalSelectors.getBuyerInfo(state),
        (state: any) => this.additionalSelectors.getCoBuyerName(state),
        (buyer, coBuyerName): ProposalCustomerInfo => mapCustomer(buyer, coBuyerName)
    );

    getTradesInfo = (state: any): PdfTradeIn[] =>
        hasSingleVehicle(state) ? this.additionalSelectors.getTradeIns(state) : NO_TRADES;
}

export const getSelectedDealTypes = (state: RootStateWithProposal): DealType[] => state.requestProposal.selectedDealTypes;

export const getSelectedIncentives = (state: RootStateWithProposal): IncentiveType[] => state.requestProposal.selectedIncentives;

export const getNotes = (state: RootStateWithProposal): string => state.requestProposal.notes;

export const getRequestStatus = (state: RootStateWithProposal): RequestProposalState['requestStatus'] =>
    state.requestProposal.requestStatus;

export const getIsRequestChangeModalOpen = (state: RootStateWithProposal): boolean =>
    state.requestProposal.isRequestChangeModalOpen;

export const getDealPdfUrl = (state: RootStateWithProposal): string | undefined => state.requestProposal?.dealPdfUrl;

/** The dealXgVersion `dealPdfUrl` was fetched for */
export const getDealPdfVersion = (state: RootStateWithProposal): string | undefined => state.requestProposal?.dealPdfVersion;

/** Whether a dealPdf fetch is in flight */
export const getDealPdfFetchStatus = (state: RootStateWithProposal): ApiRequestStatus | undefined =>
    state.requestProposal?.dealPdfFetchStatus;

export const getShowConfetti = (state: RootStateWithProposal): boolean => state.requestProposal.showConfetti;
export const getPdfLoadingStatus = (state: RootStateWithProposal): boolean => state.requestProposal.isDealPdfLoading;
